import React, { useContext } from 'react'
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { ClientBankAccount, ClientBankAccountStatus } from 'api/NPayments/Types/ClientBankAccount'
import { AuditLogIcon } from 'icons/AuditLogIcon'

import { successToast, ToastContext } from '../../../../../components/Toast/Toast'
import { useApiClient } from '../../../../../utils/api/ApiClient'
import { NPaymentsApi } from '../../../../../utils/api/nPaymentsApi'
import { AuthSessionContext } from '../../../../../utils/AuthContext'
import { hasPermission } from '../../../../../utils/permissionsValidator'

interface BankAccountsDropdownMenuProps {
  dataItem: ClientBankAccount
  onAddEditBankAccountModal(data: ClientBankAccount): void
  onDeleteBankAccountModal(data: ClientBankAccount): void
  onAuditModalOpen(data: ClientBankAccount): void
  forceRefresh(): void
}

export const BankAccountsDropdownMenu: React.FC<BankAccountsDropdownMenuProps> = (props) => {
  const { dataItem, forceRefresh } = props
  const { onAddEditBankAccountModal, onDeleteBankAccountModal, onAuditModalOpen } = props

  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const paymentsApi = useApiClient(NPaymentsApi)
  const setToast = useContext(ToastContext)

  const updateBankAccountStatus = async (
    bankAccountId: string,
    statusId: ClientBankAccountStatus
  ) => {
    await paymentsApi.setClientBankStatus(bankAccountId, statusId)
    setToast(successToast('Bank Account status updated.'))
    forceRefresh()
  }

  return (
    <>
      <DropdownMenuItem
        disabled={!hasPermission(permissions, PermissionsEnum.CanEditClientAccounts)}
        onSelect={() => onAddEditBankAccountModal(dataItem)}
      >
        Edit
      </DropdownMenuItem>
      <DropdownMenuItem
        disabled={!hasPermission(permissions, PermissionsEnum.CanEditClientAccounts)}
        onSelect={() => onDeleteBankAccountModal(dataItem)}
      >
        Delete
      </DropdownMenuItem>
      <DropdownMenuItem
        disabled={!hasPermission(permissions, PermissionsEnum.CanViewClientAccounts)}
        onSelect={() => onAuditModalOpen(dataItem)}
      >
        <AuditLogIcon style={{ width: 12, height: 12, cursor: 'pointer' }} /> Audit Log
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuSub>
        <DropdownMenuSubTrigger
          disabled={!hasPermission(permissions, PermissionsEnum.CanEditClientAccounts)}
        >
          Status
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          {dataItem.status.id !== ClientBankAccountStatus.PENDING && (
            <DropdownMenuItem
              disabled={!hasPermission(permissions, PermissionsEnum.CanEditClientAccounts)}
              onSelect={() => updateBankAccountStatus(dataItem.id, ClientBankAccountStatus.PENDING)}
            >
              Pending
            </DropdownMenuItem>
          )}
          {dataItem.status.id !== ClientBankAccountStatus.APPROVED && (
            <DropdownMenuItem
              disabled={!hasPermission(permissions, PermissionsEnum.CanEditClientAccounts)}
              onSelect={() =>
                updateBankAccountStatus(dataItem.id, ClientBankAccountStatus.APPROVED)
              }
            >
              Approve
            </DropdownMenuItem>
          )}
          {dataItem.status.id !== ClientBankAccountStatus.REJECTED && (
            <DropdownMenuItem
              disabled={!hasPermission(permissions, PermissionsEnum.CanEditClientAccounts)}
              onSelect={() =>
                updateBankAccountStatus(dataItem.id, ClientBankAccountStatus.REJECTED)
              }
            >
              Reject
            </DropdownMenuItem>
          )}
          {dataItem.status.id !== ClientBankAccountStatus.EXPIRED && (
            <DropdownMenuItem
              disabled={!hasPermission(permissions, PermissionsEnum.CanEditClientAccounts)}
              onSelect={() => updateBankAccountStatus(dataItem.id, ClientBankAccountStatus.EXPIRED)}
            >
              Expired
            </DropdownMenuItem>
          )}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    </>
  )
}
